import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useFormState } from 'react-hook-form';
import {
  AutocompleteMultiSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { useSupplyIabCategoriesQuery, useSupplyMutations } from '../../hooks';
import { SupplyStatus, type SupplyDomain } from '../../model';
import { supplyDomainFormSchema, type SupplyDomainFormValue } from '../../formSchemas';

export function SupplyDomainDialog({
  domain,
  onClose,
}: {
  domain: SupplyDomain | null;
  onClose: () => void;
}) {
  'use no memo';

  const mutations = useSupplyMutations();
  const categories = useSupplyIabCategoriesQuery();
  const form = useForm<SupplyDomainFormValue>({
    defaultValues: {
      url: domain?.url ?? '',
      status:
        domain?.status === SupplyStatus.Archived ? SupplyStatus.Archived : SupplyStatus.Active,
      iabCategories: domain?.iabCategories ?? [],
    },
    resolver: zodResolver(supplyDomainFormSchema),
  });
  const { errors } = useFormState({ control: form.control });
  const pending = mutations.createDomain.isPending || mutations.updateDomain.isPending;
  const { close: requestClose, onOpenChange } = useBusyDialogGuard(pending, onClose);
  async function submit(values: SupplyDomainFormValue) {
    const payload = { ...values, url: values.url.trim() };
    try {
      if (domain) await mutations.updateDomain.mutateAsync({ id: domain.id, payload });
      else await mutations.createDomain.mutateAsync(payload);
      toast.success(dashboardT(domain ? 'supply.domains.updated' : 'supply.domains.created'));
      onClose();
    } catch {
      form.setError('root.server', { message: dashboardT('supply.saveError') });
    }
  }
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 overflow-visible p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {domain ? dashboardT('supply.domains.edit') : dashboardT('supply.domains.create')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('supply.domains.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold" htmlFor="supply-domain-url">
                  {dashboardT('supply.url')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="supply-domain-url"
                  className="h-9"
                  required
                  aria-invalid={Boolean(errors.url)}
                  {...form.register('url')}
                />
                {errors.url?.message ? (
                  <p className="text-destructive text-xs">{errors.url.message}</p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">
                  {dashboardT('supply.status')} <span className="text-destructive">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-9" aria-label={dashboardT('supply.status')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SupplyStatus.Active}>
                          {dashboardT('supply.active')}
                        </SelectItem>
                        <SelectItem value={SupplyStatus.Archived}>
                          {dashboardT('supply.archived')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">
                  {dashboardT('supply.iabCategories')}
                </Label>
                <Controller
                  control={form.control}
                  name="iabCategories"
                  render={({ field }) => (
                    <AutocompleteMultiSelect
                      label={dashboardT('supply.iabCategories')}
                      placeholder={dashboardT('supply.chooseIabCategories')}
                      options={(categories.data ?? []).map((item) => ({
                        id: item.id,
                        label: item.name,
                      }))}
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  )}
                />
              </div>
              {errors.root?.server?.message ? (
                <p role="alert" className="text-destructive text-sm">
                  {errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={requestClose}>
                {dashboardT('supply.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {domain ? dashboardT('supply.save') : dashboardT('supply.domains.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
